import type { NotifiableError } from '@bugsnag/js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { type ErrorInfo, type ReactNode } from 'react';

import { qknColors } from 'app-components/utils/theme';

import useStartupThemeMode from 'hooks/useStartupThemeMode';
import { bugsnagNotify } from 'utils/core/bugsnag';

/*
 * Renders without the store or the theme, which may be what failed. Same colors as PersistLoading.
 */
function BootstrapErrorView(): React.JSX.Element {
  const isDarkMode = useStartupThemeMode() === 'dark';
  return (
    <Box
      role='alertdialog'
      aria-labelledby='bootstrap-error-title'
      aria-describedby='bootstrap-error-description'
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        padding: 3,
        textAlign: 'center',
        backgroundColor: isDarkMode ? qknColors.grey[950] : qknColors.grey[0],
        color: isDarkMode ? qknColors.common.white : qknColors.grey[900],
      }}
    >
      {/* No ThemeProvider here, so these use MUI's default typography. */}
      <Box component='h1' id='bootstrap-error-title' sx={{ typography: 'h5', margin: 0 }}>
        {"Simplifi couldn't start"}
      </Box>
      <Box component='p' id='bootstrap-error-description' sx={{ typography: 'body1', margin: 0 }}>
        Something went wrong while loading. Reload the page to try again.
      </Box>
      <Button
        variant='contained'
        autoFocus
        disableElevation
        sx={{ backgroundColor: qknColors.blue[600], '&:hover': { backgroundColor: qknColors.blue[700] } }}
        onClick={() => window.location.reload()}
      >
        Reload
      </Button>
    </Box>
  );
}

type Props = { children: ReactNode };
type State = { failed: boolean };

/** Catches failures above the app's own error boundary: store bootstrap, providers and the loading screen. */
export class BootstrapErrorBoundary extends React.Component<Props, State> {
  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  state: State = { failed: false };

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    // Bugsnag reports any value (non-errors as InvalidError), but its typings only accept error shapes.
    bugsnagNotify(error as NotifiableError, (event) => {
      // Runs after Bugsnag's global onError, so no error page is scheduled, but the crash still counts as unhandled.
      event.unhandled = true;
      event.severity = 'error';
      event.context = 'startup';
      event.addMetadata('react', { componentStack: errorInfo.componentStack });
    });
  }

  render(): ReactNode {
    return this.state.failed ? <BootstrapErrorView /> : this.props.children;
  }
}
